import { LabelMedium, LabelSmall, ParagraphXSmall } from "baseui/typography";
import { TDailyScheduleEntry } from "../types";
import { is0oClock, isAlmost0oClock, timespanToString } from "../../../../utils/timeUtils";
import { FormattedMessage, useIntl } from "react-intl";
import { getPrettifiedValueString, getValueOptions } from "../utils";
import { TScheduler } from "../types";
import { HorizontalStackPanel, VerticalStackPanel } from "../../../../components/Layout/StackPanel";
import { styled, useStyletron } from "baseui";
import { differenceInSeconds } from "date-fns";
import { MdOutlineTimer } from "react-icons/md";
import { isActive } from "./utils";
import { useState } from "react";
import { EditEntryDialog } from "../EditEntryDialog/EditEntryDialog";
import useIsMobile from "../../../../hooks/useIsMobile/useIsMobile";
import { StatefulPopover } from "baseui/popover";
import { Button } from "baseui/button";
import { GetObjectUrl } from "../../../../utils/navigationService";
import { Link } from "react-router-dom";


const DayItemEntry: React.FC<{
    item: TDailyScheduleEntry,
    app: TScheduler,
    now: Date,
    isFirst: boolean,
    isLast: boolean,
    totalHeight: number,
    onEventDeleted: (event: TDailyScheduleEntry) => void,
    onEventChanged: (event: TDailyScheduleEntry) => void
}> = ({ item, app, now, isFirst, isLast, onEventDeleted, onEventChanged, totalHeight }) => {

    const [css, theme] = useStyletron();
    const [isOpen, setIsOpen] = useState(false);
    const intl = useIntl();
    const startTime = new Date(item.start);
    const endTime = new Date(item.end);
    const isEndOfDay = isAlmost0oClock(endTime);
    const isStartOfDay = is0oClock(startTime);
    const isWholeDay = isStartOfDay && isEndOfDay;
    const diffInSec = differenceInSeconds(endTime, startTime);
    const isDefault = item.isDefault;
    const active = isActive(item.weekday, startTime, endTime, now);
    const diffInSecToStart = differenceInSeconds(now, startTime);
    const diffInSecToEnd = differenceInSeconds(now, endTime);
    const pxPerSecond = totalHeight / 24 / 60 / 60;
    const valueOptions = getValueOptions(app);

    const handleValueChanged = (state: { value: number | boolean | undefined | null, timeFrom: Date, timeTo: Date }, eventId: string) => {
        onEventChanged({
            end: state.timeTo,
            start: state.timeFrom,
            value: state.value,
            weekday: item.weekday,
            id: eventId,
            isDefault: false
        })
    }
    const handleDeleteEvent = () => { onEventDeleted(item) }
    const height = isWholeDay ? '100%' : `${diffInSec * pxPerSecond}px`;
    const handleEditEvent = () => { setIsOpen(true) }

    return (<>
        <PopoverContainer isDefault={item.isDefault} startTime={item.start} endTime={item.end} onEditEvent={handleEditEvent} onDeleteEvent={handleDeleteEvent}>
            <MainContainer $first={isFirst} $last={isLast} $height={height} onClick={() => isDefault ? undefined : setIsOpen(true)} $isDefault={isDefault} >
                <TimeContainer>{isWholeDay ?
                    <ParagraphXSmall margin={0}><FormattedMessage id="Scheduler.AllDay" defaultMessage="All Day" /></ParagraphXSmall>
                    : <>
                        <ParagraphXSmall margin={0}>
                            {isStartOfDay ?
                                <FormattedMessage id='Scheduler.StartOfDay' defaultMessage={'start of day'}></FormattedMessage>
                                : <>{startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                            }
                        </ParagraphXSmall>
                        <ParagraphXSmall color={theme.colors.contentSecondary} margin={0}>
                            {isEndOfDay ?
                                <FormattedMessage id='Scheduler.EndOfDay' defaultMessage={'end of day'}></FormattedMessage>
                                : <HorizontalStackPanel $flexWrap={'nowrap'}><MdOutlineTimer />{timespanToString(diffInSec * 1000, true)}</HorizontalStackPanel>
                            }
                        </ParagraphXSmall>
                    </>
                }</TimeContainer>

                <ContentContainer>
                    <Value active={active} app={app} item={item} ></Value>
                </ContentContainer>

                {active && <CurrentTime $top={`${diffInSecToStart / 200}px`}>
                    <HorizontalStackPanel $flexWrap={'nowrap'} $alignItems={'flex-start'}>
                        <div className={css({ width: '50px', height: `2px`, borderRadius: '2px', background: theme.colors.positive300 })}></div>
                        <ParagraphXSmall margin={0} marginTop={"-12px"} color={theme.colors.positive}>{<FormattedMessage id='now' defaultMessage={'Now'}></FormattedMessage>} {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</ParagraphXSmall>
                    </HorizontalStackPanel>
                </CurrentTime>}

                <div></div>

            </MainContainer>

        </PopoverContainer>

        {isOpen && <EditEntryDialog
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            onAccept={handleValueChanged}
            onDelete={handleDeleteEvent}
            valueOptions={valueOptions}
            value={item.value} eventId={item.id} dataType={app.dataType} timeFrom={startTime} timeTo={endTime}>
        </EditEntryDialog>}
    </>)
};

export default DayItemEntry;

const TimeContainer = styled<'div', {}>("div", ({ $theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    margin: $theme.sizing.scale200
}));

const ContentContainer = styled<'div', {}>("div", ({ $theme }) => ({
    margin: $theme.sizing.scale200,
    height: '100%'
}));

const MainContainer = styled<'div', { $isDefault, $height, $first, $last }>("div", ({ $theme, $isDefault, $height, $first, $last }) => ({
    gridColumnStart: 1,
    gridColumnEnd: 3,
    display: 'flex',
    flexDirection: 'column',
    //gridColumn: 2,
    position: 'relative',
    justifyContent: 'space-between',
    cursor: $isDefault ? undefined : 'pointer',
    ":hover": {
        background: $isDefault ? undefined : $theme.colors.backgroundAccentLight
    },
    background: $isDefault ? $theme.colors.backgroundTertiary : $theme.colors.backgroundAccentLight,
    borderTopLeftRadius: $first ? $theme.sizing.scale400 : undefined,
    borderTopRightRadius: $first ? $theme.sizing.scale400 : undefined,
    borderBottomLeftRadius: $last ? $theme.sizing.scale400 : undefined,
    borderBottomRightRadius: $last ? $theme.sizing.scale400 : undefined,
    minHeight: $height//'100%'
}));

const Value: React.FC<{ item: TDailyScheduleEntry, app: TScheduler, active: boolean }> =
    ({ item, app, active }) => {
        const [css, theme] = useStyletron();
        const intl = useIntl();
        const isDefault = item.isDefault;
        const value = getPrettifiedValueString(app.dataType, item.value, app.stateTextList, intl);
        const prettyDefaultValue = getPrettifiedValueString(app.dataType, app.defaultValue, app.stateTextList, intl);
        const defaultValueUrl = `${GetObjectUrl(app.resourceGroupId, app.id)}?defaultView=configuration`;
        if (isDefault)
            return (
                <DefaultValueContainer>
                    <LabelSmall>{prettyDefaultValue}</LabelSmall>
                    <Link to={defaultValueUrl} className={css({ textDecoration: 'none' })}>
                        <ParagraphXSmall color={theme.colors.contentTertiary} margin={0}><u>{intl.formatMessage({ id: "Scheduler.DefaultValue", defaultMessage: "Default Value" })}</u></ParagraphXSmall>
                    </Link>
                </DefaultValueContainer>
            )
        return (
            <NormalValueContainer>
                <LabelMedium>{value}</LabelMedium>
            </NormalValueContainer>
        )
    };

const PopoverContainer: React.FC<{ startTime: Date, endTime: Date, isDefault: boolean, children, onDeleteEvent: () => void, onEditEvent: () => void }> = (props) => {
    const isMobile = useIsMobile();
    const [css, theme] = useStyletron()
    if (isMobile) return <>{props.children}</>


    const handleDelete = (e: any) => { e.stopPropagation(); props.onDeleteEvent() }
    const handleEdit = (e: any) => { e.stopPropagation(); props.onEditEvent() }

    return <StatefulPopover
        content={() => (
            <div className={css({ padding: theme.sizing.scale200 })}>
                <VerticalStackPanel $alignItems={'center'}>
                    <HorizontalStackPanel>
                        <ParagraphXSmall>
                            {props.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {' - '}
                            {props.endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </ParagraphXSmall>
                    </HorizontalStackPanel>
                    {!props.isDefault && <HorizontalStackPanel>

                        <Button onClick={handleEdit} size='compact'><FormattedMessage id="edit"></FormattedMessage></Button>
                        <Button onClick={handleDelete} size='compact' overrides={{
                            BaseButton: {
                                style: ({ $theme }) => ({
                                    backgroundColor: $theme.colors.negative
                                })
                            }
                        }}><FormattedMessage id="delete"></FormattedMessage></Button>
                    </HorizontalStackPanel>}
                </VerticalStackPanel>
            </div>
        )}
        triggerType={'hover'}
        returnFocus
        autoFocus
    >
        {props.children}
    </StatefulPopover>
}


const CurrentTime = styled<'div', { $top }>("div", ({ $theme, $top }) => ({
    position: 'absolute',
    top: $top,
}));

const DefaultValueContainer = styled<'div', {}>("div", ({ $theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: $theme.sizing.scale200,
    flexWrap: 'wrap'
}));

const NormalValueContainer = styled<'div', {}>("div", ({ $theme }) => ({
    height: '100%',
}));



